import classNames from 'classnames';
import React, { HTMLAttributes } from 'react';

import styles from './BusyIndicator.module.css';
import { Size } from '../../types';

interface BusyIndicatorProps extends Pick<
  HTMLAttributes<HTMLDivElement>,
  'className' | 'style'
> {
  /**
   * The size of the busy indicator.
   *
   * @default "M"
   */
  size?: Omit<Size, 'XS'>;
  /**
   * The title of the busy indicator.
   */
  title?: string;
}

const sizingClassBySize = new Map<Omit<Size, 'XS'>, string>([
  ['S', styles.small],
  ['M', styles.medium],
  ['L', styles.large],
  ['XL', styles.extraLarge],
]);

/**
 * The Busy indicator signals that an operation is underway
 * and the user must wait for it to complete.
 * It does not block the current UI screen so other operations
 * could be triggered in parallel.
 * It displays 3 dots and each dot expands and shrinks at a different rate,
 * resulting in a cascading flow of animation.
 */
export function BusyIndicator({
  size = 'M',
  title = 'Please wait',
  className,
  style,
}: BusyIndicatorProps) {
  const sizingClass = sizingClassBySize.get(size);

  return (
    <div
      aria-valuemax={100}
      aria-valuemin={0}
      aria-valuetext="Busy"
      className={classNames(
        styles['busy-indicator-circles-wrapper'],
        sizingClass,
        className,
      )}
      role="progressbar"
      style={style}
      title={title}
    >
      {[0, 1, 2].map((i) => (
        <div
          className={styles['busy-indicator-circle']}
          key={i}
        />
      ))}
    </div>
  );
}
